import { AccessTime, CalendarMonth, LocationOn, OpenInNew, People, Repeat, Schedule } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, Paper, Stack, Tooltip, Typography, alpha } from "@mui/material";
import { useEffect, useState } from "react";
import { selectCurrentCompany } from "../../../../../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../../../../../redux/hooks/redux";
import { formatDate } from "../../../../../../../../utils";
import { BaseModalComponent } from "../../../../../../../common/BaseModalComponent";
import { JobLayout } from "../../../../../../../jobs/jobs-list/jobs-layout/Job.layout";
import { flownectColors } from "../../../../../const";
import { getFrequencyDescription } from "../../../../../utils/getFrequencyDescription";
import { formatCompanyInstant } from "../../../../../utils/companyWallTime";
import { InfoRow } from "./InfoRow";
import { JobDetModal } from "@components/jobs/jobs-list/job-card/JobDetModal";

export const EventJobModal = ({ arg, softBorders = false }) => {
  const currentCompany = useAppSelector(selectCurrentCompany);
  const companyTimeZone = currentCompany?.timeZone || "UTC";
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return formatCompanyInstant(dateStr, companyTimeZone, "MMM dd, yyyy • h:mm a");
  };

  const getDurationString = (startDate, endDate) => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;

    const minutes = Math.round((end - start) / 60000);

    if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"}`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;

    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"}`;
  };

  const formatElapsed = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const h = Math.floor(safeSeconds / 3600);
    const m = Math.floor((safeSeconds % 3600) / 60);
    const s = safeSeconds % 60;

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");

    return `${hh}:${mm}:${ss}`;
  };

  if (!arg?.event) return null;

  const {
    title,
    extendedProps: { status, workType, event: jobEvent, workforce, coverageArea, jobId, color, quote, client },
  } = arg.event;

  const job = { workforce, id: jobId, workType, quote, ...arg?.event?.extendedProps?.job };

  const baseColor = color || "#1976d2";
  const borderColor = alpha(baseColor, 0.8);
  const contentX = softBorders ? 1.25 : 2.5;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: softBorders ? 2 : 3,
        overflow: "hidden",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        width: "100%",
        maxHeight: "85vh",
        display: "flex",
        flexDirection: "column",
        containerType: "inline-size",
        border: "none",
      }}
    >
      <Box
        sx={{
          overflow: "hidden",
          position: "relative",
          background: "var(--fx-surface-base, #FFFFFF)",
          color: flownectColors.gray[900],
          px: contentX,
          py: softBorders ? 1 : 2,
          borderBottom: "none",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, mr: 2, "@container (max-width: 520px)": { mr: 1 } }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                opacity: softBorders ? 1 : 0.9,
                fontSize: "0.65rem",
                color: flownectColors.gray[700],
              }}
            >
              {workType || "Job"} • Job #{jobId}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                mt: 0.25,
                lineHeight: 1.3,
                fontSize: softBorders ? 11 : "0.92rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                "@container (max-width: 520px)": {
                  whiteSpace: "normal",
                  overflow: "visible",
                  textOverflow: "clip",
                  display: "block",
                  wordBreak: "break-word",
                },
              }}
            >
              {client?.firstName || client?.lastName ? `${client.firstName || ""} ${client.lastName || ""}`.trim() : client?.alias || `Job #${jobId}`}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
            <Chip
              label={status?.toUpperCase() || "UNKNOWN"}
              size="small"
              sx={{
                bgcolor: alpha(baseColor, 0.08),
                color: baseColor,
                fontWeight: 700,
                fontSize: "0.6rem",
                height: 24,
                border: "none",
                backdropFilter: "none",
              }}
            />

            <BaseModalComponent
              modalKey="Calendar-JobsEvents"
              modalLabel={`Job ${jobId}`}
              elementTrigger={
                <Tooltip
                  title="View job details"
                  arrow
                  placement="top"
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: flownectColors.gray[900],
                        color: "#fff",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                      },
                    },
                    arrow: {
                      sx: {
                        color: flownectColors.gray[900],
                      },
                    },
                    popper: {
                      sx: { zIndex: 9999999 },
                    },
                  }}
                >
                  <JobDetModal
                    job={job}
                    modalKey={`CalendarEvent-JobCard-${job?.id}`}
                    wrapTrigger={false}
                    trigger={
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          <OpenInNew
                            sx={{
                              fontSize: softBorders ? 11 : 13,
                              "@container (max-width: 520px)": {
                                fontSize: 11,
                              },
                            }}
                          />
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: softBorders ? "0.6rem" : "0.66rem",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          px: softBorders ? 0.75 : 1,
                          py: 0.3,
                          minWidth: softBorders ? 62 : 0,
                          minHeight: 24,
                          flexShrink: 0,
                          boxShadow: "none",
                          bgcolor: baseColor,
                          "& .MuiButton-startIcon": { mr: 0.5 },
                          "&:hover": { bgcolor: borderColor },
                          "@container (max-width: 520px)": {
                            width: 24,
                            minWidth: 24,
                            height: 24,
                            minHeight: 24,
                            p: 0,
                            borderRadius: 1.5,
                            boxShadow: "none",
                            "& .MuiButton-startIcon": { m: 0 },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            "@container (max-width: 520px)": {
                              display: "none",
                            },
                          }}
                        >
                          View Job
                        </Box>
                      </Button>
                    }
                  />
                </Tooltip>
              }
            >
              <JobLayout showCalendar={false} job={job} currentEventUid={jobEvent?.uid} />
            </BaseModalComponent>
          </Stack>
        </Stack>

        <Box
          sx={{
            width: "100%",
            height: "1.5px",
            bgcolor: "#b6c0cc",
            opacity: 1,
            mt: 1,
          }}
        />
      </Box>

      <CardContent
        sx={{
          px: contentX,
          pt: softBorders ? 0.75 : 1,
          pb: softBorders ? 1 : 2.5,
          flex: 1,
          overflowY: "auto",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
        }}
      >
        <Stack spacing={softBorders ? 1 : 2}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={softBorders ? 1 : 1.5}>
            <Typography
              variant="body1"
              sx={{
                color: flownectColors.gray[900],
                fontWeight: 500,
                fontSize: softBorders ? 11 : "0.92rem",
                lineHeight: 1.35,
                flex: 1,
                minWidth: 0,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title || job?.alias || "No title"}
            </Typography>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              width: "100%",
              p: softBorders ? 0.25 : 2.5,
              borderRadius: softBorders ? 0 : 2,
              border: "none",
              "@container (max-width: 520px)": {
                p: 0.5,
              },
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                width: "100%",
                columnGap: softBorders ? 1.25 : 3,
                rowGap: softBorders ? 1.25 : 2.5,
                alignItems: "start",
                "& > :nth-of-type(1)": { justifySelf: "start" },
                "& > :nth-of-type(2)": { justifySelf: "center" },
                "& > :nth-of-type(3)": { justifySelf: "center", transform: "translateX(24px)" },
                "@container (max-width: 520px)": {
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  columnGap: 0.5,
                  justifyItems: "start",
                  "& > :nth-of-type(3)": { transform: "none" },
                },
              }}
            >
              <InfoRow compact={softBorders} icon={CalendarMonth} label="Start Date" value={formatDateTime(jobEvent?.startDate)} />
              {jobEvent?.endDate && <InfoRow compact={softBorders} icon={AccessTime} label="End Date" value={formatDateTime(jobEvent?.endDate)} />}
              <InfoRow compact={softBorders} icon={Schedule} label="Duration" value={getDurationString(jobEvent?.startDate, jobEvent?.endDate)} />
              {getFrequencyDescription(jobEvent) && (
                <InfoRow compact={softBorders} icon={Repeat} label="Frequency" value={getFrequencyDescription(jobEvent)} />
              )}
            </Box>
          </Paper>

          {(coverageArea?.address || workforce?.length > 0) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
                alignItems: "start",
                mt: "24px !important",
              }}
            >
              {coverageArea?.address && (
                <Box
                  sx={{
                    px: softBorders ? 0 : 1.5,
                    py: softBorders ? 0 : 1.5,
                    minHeight: softBorders ? 28 : undefined,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 1.5,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    border: "none",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <LocationOn sx={{ fontSize: softBorders ? 14 : 16, color: baseColor, mt: 0.25 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: flownectColors.gray[800],
                        fontWeight: 500,
                        fontSize: softBorders ? 12 : "0.875rem",
                        lineHeight: softBorders ? 1.35 : 1.4,
                        "@container (max-width: 520px)": {
                          fontSize: "0.68rem",
                          lineHeight: 1.25,
                        },
                      }}
                    >
                      {coverageArea.address}
                    </Typography>
                  </Stack>
                </Box>
              )}

              {workforce?.length > 0 && (
                <Box
                  sx={{
                    p: 0,
                    mt: coverageArea?.address ? "24px !important" : 0,
                    borderRadius: softBorders ? 1.5 : 0,
                    bgcolor: "transparent",
                    border: "none",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={0}>
                    <People sx={{ fontSize: softBorders ? 14 : 16, color: baseColor }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: flownectColors.gray[700],
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: softBorders ? 10 : "0.7rem",
                        lineHeight: 1.1,
                      }}
                    >
                      Workforce ({workforce.length})
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: softBorders ? "wrap" : "nowrap",
                      justifyContent: "flex-start",
                      gap: softBorders ? 1 : 1.5,
                      mt: 0.35,
                      overflowX: "auto",
                      pb: 0,
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-track": { bgcolor: flownectColors.gray[100], borderRadius: 3 },
                      "&::-webkit-scrollbar-thumb": { bgcolor: flownectColors.gray[400], borderRadius: 3 },
                    }}
                  >
                    {workforce.map((worker, index) => {
                      const alias = `${worker?.firstName ?? ""} ${worker?.lastName ?? ""}`.trim();
                      const role = worker?.WorkforcesByJobs?.jobWorkforceRole ?? "employee";

                      const workforceEvent = worker?.WorkforcesByJobEvents || {};
                      const rawWorkforceStatus = String(workforceEvent?.status ?? "");
                      const normalizedWorkforceStatus = rawWorkforceStatus.toLowerCase();

                      const workerStartAt = workforceEvent?.startAt;
                      const workerEndAt = workforceEvent?.endAt;

                      const isCompleted = normalizedWorkforceStatus === "completed" || (!!workerStartAt && !!workerEndAt);

                      const isInProgress =
                        !isCompleted &&
                        (normalizedWorkforceStatus === "inprogress" ||
                          normalizedWorkforceStatus === "in_progress" ||
                          (!!workerStartAt && !workerEndAt));

                      const elapsedSeconds =
                        isInProgress && workerStartAt ? Math.max(0, Math.floor((now - new Date(workerStartAt).getTime()) / 1000)) : 0;

                      return (
                        <Box
                          key={index}
                          sx={{
                            minWidth: softBorders ? "fit-content" : 140,
                            maxWidth: softBorders ? "100%" : undefined,
                            px: softBorders ? 1 : 1.5,
                            py: softBorders ? 0.65 : 1.5,
                            borderRadius: 1.5,
                            bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            border: "none",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: softBorders ? "flex-start" : "center",
                            gap: softBorders ? 0.35 : 0.35,
                            textAlign: softBorders ? "left" : "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: flownectColors.gray[600],
                              textTransform: "capitalize",
                              fontSize: softBorders ? 9 : "0.65rem",
                              lineHeight: 1.1,
                            }}
                          >
                            {role}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: flownectColors.gray[800],
                              lineHeight: 1.2,
                              fontSize: softBorders ? 10 : "0.7rem",
                            }}
                            noWrap
                          >
                            {alias}
                          </Typography>

                          {isInProgress && workerStartAt && (
                            <Box
                              sx={{
                                mt: 0.15,
                                px: 0,
                                py: 0,
                                borderRadius: 0,
                                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                                border: "none",
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: baseColor,
                                  fontWeight: 400,
                                  fontSize: softBorders ? "0.58rem" : "0.62rem",
                                  lineHeight: 1.25,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatElapsed(elapsedSeconds)}
                              </Typography>
                            </Box>
                          )}

                          {isCompleted && workerStartAt && workerEndAt && (
                            <Box
                              sx={{
                                mt: 0.15,
                                width: softBorders ? "auto" : "100%",
                                px: 0,
                                py: 0,
                                borderRadius: 0,
                                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                                border: "none",
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: flownectColors.gray[700],
                                  fontWeight: 400,
                                  fontSize: softBorders ? "0.58rem" : "0.62rem",
                                  lineHeight: 1.25,
                                }}
                              >
                                Start {formatDate(workerStartAt, companyTimeZone, "h:mm a")}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: flownectColors.gray[700],
                                  fontWeight: 400,
                                  fontSize: softBorders ? "0.58rem" : "0.62rem",
                                  lineHeight: 1.25,
                                }}
                              >
                                Finish {formatDate(workerEndAt, companyTimeZone, "h:mm a")}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
